import { t } from "@lingui/macro";
import React, { useEffect, useMemo } from "react";

import { ChevronDownIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  Divider,
  VStack,
  Text,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

export type RateOption = {
  id: string;
  name: string;
  code: string;
  visibility?: "NONE" | "ALL" | "QUALIFYING";
  chargeRuleSets?: ChargeRuleSet[];
};

export type ChargeRule = {
  id: string;
  duration: number;
  minPlayers: number;
  price: number;
};

export type ChargeRuleSet = {
  chargeRules: ChargeRule[];
};

type RateDropdownProps = {
  selectedRateId?: string | null;
  placeholder?: string;
  onChange: (value: string) => void;
  variant?: "Default" | "Box";
  testID?: string;
  disabled?: boolean;
  rates: {
    qualifying: RateOption[];
    nonQualifying: RateOption[];
  } | null;
  expanded?: boolean;
};

export type GroupedOption = {
  label: string;
  options: RateOption[];
};

export type DropdownSelectOption = {
  value: string;
  label: string | React.JSX.Element | Array<React.JSX.Element>;
  // allows override of label when option is selected
  selectedLabel?: Element | Element[];
  isDisabled?: boolean;
};

const flatOptions = (options: GroupedOption[]): RateOption[] =>
  options.reduce((acc, cur) => {
    return acc.concat(cur.options);
  }, [] as RateOption[]);

export const RateDropdown = ({
  disabled,
  selectedRateId,
  onChange,
  placeholder,
  variant,
  testID,
  rates,
  expanded,
}: RateDropdownProps) => {
  const options = useMemo(
    () => [
      {
        label: t`Recommended`,
        options: rates?.qualifying || [],
      },
      {
        label: t`Other`,
        options: rates?.nonQualifying || [],
      },
    ],

    [rates]
  );

  const numRates = useMemo(() => flatOptions(options).length, [options]);
  const currentSelectedOption = useMemo(() => {
    const defaultRate = rates?.qualifying[0];
    return (
      flatOptions(options).find((o) => o.id === selectedRateId) || defaultRate
    );
  }, [options, selectedRateId, rates]);

  const displayValue = currentSelectedOption
    ? expanded
      ? `${currentSelectedOption.code}`
      : currentSelectedOption.code ?? currentSelectedOption.name
    : placeholder ?? "Select";

  /**
   * Ensures that if we find a default - we actually set it.
   */
  useEffect(() => {
    if (!selectedRateId && currentSelectedOption) {
      onChange(currentSelectedOption.id);
    }
  }, [selectedRateId, currentSelectedOption, onChange]);

  return numRates > 1 ? (
    <VStack alignSelf="flex-start" flex={1}>
      <Menu
        defaultIsOpen={false}
        openPosition="bottom-end"
        verticalOffset={4}
        trigger={({ onPress, isOpen }) => {
          return (
            <Pressable
              disabled={disabled}
              aria-label={t`More options menu`}
              accessibilityRole="button"
              accessibilityState={{ disabled, expanded: isOpen }}
              testID={testID}
              onPress={onPress}
            >
              <HStack
                bg="grayscale-0"
                borderColor="grayscale-500"
                borderWidth={2}
                py={1}
                px={3}
                alignContent="center"
                justifyContent={"space-between"}
                style={{ borderRadius: variant === "Box" ? 8 : 40 }}
              >
                <Box alignItems="center" justifyContent="center">
                  {displayValue}
                </Box>
                <Box alignItems="center" justifyContent="center" mx={1}>
                  <ChevronDownIcon accessible={false} width={12} height={12} />
                </Box>
              </HStack>
            </Pressable>
          );
        }}
        content={({ closeMenu }) => (
          <Menu.Content size="regular" maxHeight={224}>
            <VStack accessibilityRole="menu">
              {options.map((group, i) => {
                return (
                  !!group.options.length && (
                    <Box key={group.label}>
                      {!!i && <Divider mb={1} />}
                      <Text
                        variant="mainTiny"
                        paddingHorizontal={3}
                        color="text-secondary"
                        paddingVertical={1}
                      >
                        {group.label}
                      </Text>
                      {group.options.map((o) => {
                        return (
                          <Pressable
                            key={o.id}
                            accessibilityRole="menuitem"
                            onPress={() => {
                              onChange(o.id);
                              closeMenu();
                            }}
                            padding={3}
                            _hover={{ backgroundColor: "surface-secondary" }}
                            _focus={{ backgroundColor: "surface-secondary" }}
                          >
                            <HStack
                              justifyContent="space-between"
                              maxWidth="100%"
                            >
                              <Text
                                variant="mainBaseSemiBold"
                                color="text-main-primary"
                              >
                                {o.code}
                              </Text>
                              <Text color="text-main-primary" paddingLeft={8}>
                                {o.name}
                              </Text>
                            </HStack>
                          </Pressable>
                        );
                      })}
                    </Box>
                  )
                );
              })}
            </VStack>
          </Menu.Content>
        )}
      />
    </VStack>
  ) : null;
};
